import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { ModelCard, Loader, Overline, Breadcrumbs } from "@/components/shared";
import { AdSlot } from "@/components/Ads";

export default function Models() {
  const [items, setItems] = useState(null);
  const [sort, setSort] = useState("rating");
  useSEO({ title: "AI Model Database (2026)", description: "Track the major AI models — GPT, Claude, Gemini, Grok, DeepSeek, Llama and more — with context, capabilities and pricing." });
  useEffect(() => { setItems(null); api.models({ sort }).then((d) => setItems(d.items)); }, [sort]);

  return (
    <div className="klyroo-radial">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "AI Models" }]} />
        <Overline>Model Database</Overline>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2">AI Models</h1>
        <p className="text-slate-400 mt-3 max-w-2xl text-lg">The frontier and open models powering 2026 — with context windows, capabilities, availability and pricing, editable from the KLYROO CMS.</p>

        <div className="flex items-center gap-2 mt-8 mb-6">
          <span className="text-xs font-mono text-slate-500">Sort</span>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="models-sort"
            className="bg-klyroo-surface border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white outline-none">
            <option value="rating">Rating</option><option value="newest">Newest</option>
          </select>
        </div>

        {!items ? <Loader /> : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="models-grid">
              {items.flatMap((m, i) => {
                const cells = [<ModelCard key={m.slug} m={m} />];
                if ((i + 1) % 8 === 0 && i !== items.length - 1) {
                  cells.push(<AdSlot key={`feed-ad-${i}`} placement="feed-square" className="h-full" />);
                }
                return cells;
              })}
            </div>
            <div className="mt-12 overflow-x-auto rounded-2xl border border-white/10">
              <table className="w-full text-sm min-w-[720px]">
                <thead><tr className="bg-white/5 text-left">
                  {["Model", "Company", "Context", "Reasoning", "Coding", "Multimodal", "Pricing"].map((h) => <th key={h} className="p-4 font-display font-bold text-white">{h}</th>)}
                </tr></thead>
                <tbody>
                  {items.map((m) => (
                    <tr key={m.slug} className="border-t border-white/8 hover:bg-white/[0.03]">
                      <td className="p-4"><a href={`/ai-models/${m.slug}`} className="text-klyroo-cyan font-semibold">{m.name}</a></td>
                      <td className="p-4 text-slate-300">{m.company}</td>
                      <td className="p-4 text-slate-300 font-mono text-xs">{m.context}</td>
                      <td className="p-4 text-slate-300">{m.reasoning}</td>
                      <td className="p-4 text-slate-300">{m.coding}</td>
                      <td className="p-4 text-slate-300">{m.multimodal}</td>
                      <td className="p-4 text-slate-400 text-xs">{m.pricing}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
